// The Catalog page's articles (#2007): every article of the business under its product group, and
// the dialog that adds one or changes the values an admin maintains.
import React from "react";
import { Banner, Button, Dialog, FormField, Input, Switch, Textarea } from "@/ds";
import { Icon, eur } from "@/lib";
import { cockpit } from "@/api";
import type { WorkflowBusiness } from "@/api";
import type { PushToast } from "@/notifications/toast-queue";
import { useT } from "../i18n";
import {
  catalogGroupNames,
  catalogSummary,
  centsFromEuroText,
  englishName,
  euroTextFromCents,
  groupCatalog,
  isManualItem,
  vatRateFromText,
  type CatalogItem,
} from "./catalog-articles.logic";
import styles from "./screen-catalog.module.css";

export interface CatalogArticlesProps {
  /** `null` until the business data has loaded. */
  items: readonly CatalogItem[] | null;
  /** The read failed; the page shows why above the list. */
  failed: boolean;
  /** The owner's import sits further down the page; an admin has no import to be sent to. */
  canImport: boolean;
  onEdit: (item: CatalogItem) => void;
}

export function CatalogArticles({ items, failed, canImport, onEdit }: CatalogArticlesProps) {
  const t = useT();
  const [query, setQuery] = React.useState("");
  // Browsing remembers only groups explicitly opened, so late-arriving groups stay collapsed.
  // Search keeps its own folded groups; clearing it restores the browsing choices.
  const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(new Set());
  const [foldedInSearch, setFoldedInSearch] = React.useState<ReadonlySet<string>>(new Set());
  const searching = Boolean(query.trim());
  const isOpen = (key: string) => searching ? !foldedInSearch.has(key) : expanded.has(key);
  const setOpen = (key: string, open: boolean) => (searching ? setFoldedInSearch : setExpanded)((current) => {
    const remember = searching ? !open : open;
    if (current.has(key) === remember) return current;
    const next = new Set(current);
    if (remember) next.add(key); else next.delete(key);
    return next;
  });
  const countLabel = (count: number) => t(count === 1 ? "catalog.group.count.one" : "catalog.group.count", { count });
  const summary = React.useMemo(() => (items ? catalogSummary(items) : null), [items]);
  const groups = React.useMemo(() => (items ? groupCatalog(items, query) : []), [items, query]);
  const shown = groups.reduce((count, group) => count + group.items.length, 0);
  const placeholder = failed ? "—" : t("common.loading");
  const state = failed ? "error" : !items ? "loading" : shown > 0 ? "populated" : "empty";

  return (
    <>
      <dl className={styles.summary} data-testid="catalog-summary" data-state={summary ? "loaded" : failed ? "error" : "loading"}>
        <div>
          <dt>{t("businesses.catalog.active")}</dt>
          <dd>{summary ? `${summary.active} / ${summary.total}` : placeholder}</dd>
        </div>
        <div>
          <dt>{t("catalog.summary.groups")}</dt>
          <dd>{summary ? summary.groups : placeholder}</dd>
        </div>
      </dl>

      <section className={styles.browse} aria-labelledby="catalog-articles-title">
        {/* #2187: the search stands beside the section title, not on a row of its own. */}
        <div className={styles.browseHead}>
          <h2 id="catalog-articles-title" className="catalog-section-title">{t("catalog.articles.title")}</h2>
          <Input
            type="search"
            value={query}
            placeholder={t("catalog.search.placeholder")}
            aria-label={t("catalog.search.placeholder")}
            data-testid="catalog-search"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setQuery(event.currentTarget.value);
              setFoldedInSearch(new Set());
            }}
          />
        </div>
        <div className="catlist" data-testid="catalog-articles" data-state={state} role="region" aria-label={t("businesses.catalog.resultsLabel")}>
          {/* How many articles a search leaves is announced, not shown: each group shows its own count. */}
          <span className="sr-only" data-testid="catalog-count" aria-live="polite">{items ? countLabel(shown) : ""}</span>
          {state === "loading" && (
            <p className="catlist__activity" role="status" aria-live="polite">{t("businesses.catalog.loadingResults")}</p>
          )}
          {state === "empty" && (
            <p className="subtle catlist__empty">
              {query.trim()
                ? t("businesses.catalog.noResults")
                : t(canImport ? "catalog.empty.owner" : "catalog.empty.admin")}
            </p>
          )}
          {groups.map((group) => (
            <details key={group.key} className={styles.group} open={isOpen(group.key)} data-testid="catalog-group">
              <summary className={styles.groupHead} onClick={(event) => {
                // Native toggle events are deferred and can arrive after search has been cleared.
                // Handle pointer/keyboard activation here so it updates the intended browsing mode.
                event.preventDefault();
                setOpen(group.key, !isOpen(group.key));
              }}>
                <Icon name="chevronRight" size={16} aria-hidden="true" />
                <span className={styles.groupName}>{group.name ?? t("catalog.group.none")}</span>
                <span className={styles.groupCount}>{countLabel(group.items.length)}</span>
              </summary>
              <div className="catlist__scroll">
                <table className={`neo-table catlist__table ${styles.groupTable}`}>
                  <colgroup>
                    <col className={styles.colId} />
                    <col />
                    <col className={styles.colPrice} />
                    <col className={styles.colStatus} />
                    <col className={styles.colAction} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t("catalog.col.number")}</th>
                      <th>{t("businesses.catalog.colName")}</th>
                      <th className="catlist__price">{t("businesses.catalog.colPrice")}</th>
                      <th>
                        {t("businesses.catalog.availability")}{" "}
                        <span className={styles.characteristicsLabel}>{t("businesses.catalog.characteristics")}</span>
                      </th>
                      <th><span className="sr-only">{t("catalog.col.action")}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((item) => {
                      const translated = englishName(item);
                      const price = item.price_minor === null ? null : item.price_minor / 100;
                      return (
                        <tr key={item.item_id} className={item.active ? undefined : "catlist__row--inactive"}>
                          <td className="neo-mono catlist__id" data-label={t("catalog.col.number")}>{item.bp_id ?? "—"}</td>
                          <td className="catlist__name-cell">
                            <span className="catlist__name">{item.name}</span>
                            {translated && <span className="catlist__name-en">{translated}</span>}
                            {item.subgroup && <span className="catlist__name-en">{item.subgroup}</span>}
                          </td>
                          <td className="catlist__price" data-label={t("businesses.catalog.priceShort")}>
                            <span className="catlist__price-value">
                              {eur(price)}
                              {price !== null && item.unit ? ` / ${item.unit}` : ""}
                            </span>
                          </td>
                          <td className="catlist__status" data-label={t("businesses.catalog.availability")}>
                            <div className={styles.dimensions}>
                              <span className={styles.availability}>
                                {item.active
                                  ? <span className="catlist__flag catlist__flag--active">{t("businesses.catalog.flagActive")}</span>
                                  : <span className="catlist__flag catlist__flag--off">{t("businesses.catalog.flagInactive")}</span>}
                              </span>
                              <span className={`catlist__flags ${styles.characteristics}`} role="group" aria-label={t("businesses.catalog.characteristics")}>
                                {item.is_package && <span className="catlist__flag">{t("businesses.catalog.flagPackage")}</span>}
                                {item.derived_vegan && <span className="catlist__flag">{t("businesses.catalog.flagVegan")}</span>}
                                {item.derived_vegetarian && <span className="catlist__flag">{t("businesses.catalog.flagVegetarian")}</span>}
                                {isManualItem(item) && <span className="catlist__flag">{t("catalog.flag.manual")}</span>}
                              </span>
                            </div>
                          </td>
                          <td className={styles.action}>
                            <Button variant="ghost" size="sm" iconOnly aria-label={t("catalog.edit", { name: item.name })}
                              data-testid="catalog-edit-article" onClick={() => onEdit(item)}>
                              <Icon name="pencil" size={16} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}

export interface CatalogArticleDialogProps {
  /** The article being changed; `null` adds a new one. */
  item: CatalogItem | null;
  /** Every article of the business, for the product groups already in use. */
  items: readonly CatalogItem[];
  onClose: () => void;
  onSaved: (next: WorkflowBusiness) => void;
  /** The article changed since the dialog opened; the page reads the catalog again. */
  onStale: () => void;
  pushToast: PushToast;
}

export function CatalogArticleDialog({ item, items, onClose, onSaved, onStale, pushToast }: CatalogArticleDialogProps) {
  const t = useT();
  const groupListId = React.useId();
  const manual = item ? isManualItem(item) : false;
  const [name, setName] = React.useState(item?.name ?? "");
  const [group, setGroup] = React.useState(item?.group ?? "");
  const [price, setPrice] = React.useState(euroTextFromCents(item?.price_minor ?? null));
  const [vat, setVat] = React.useState(item?.vat_rate == null ? "" : String(item.vat_rate));
  const [unit, setUnit] = React.useState(item?.unit ?? "");
  const [description, setDescription] = React.useState(item?.description ?? "");
  const [active, setActive] = React.useState(item?.active ?? true);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const groupNames = React.useMemo(() => catalogGroupNames(items), [items]);

  const priceMinor = price.trim() ? centsFromEuroText(price) : null;
  const vatRate = vat.trim() ? vatRateFromText(vat) : null;
  const priceInvalid = Boolean(price.trim()) && priceMinor === null;
  const vatInvalid = Boolean(vat.trim()) && vatRate === null;
  const nextUnit = unit.trim() || null;
  const nextDescription = description.trim() ? description : null;
  // Saving re-queues the open requests of the business, so an unchanged article is not saved again.
  const unchanged = item !== null
    && priceMinor === item.price_minor
    && vatRate === item.vat_rate
    && nextUnit === (item.unit?.trim() || null)
    && nextDescription === (item.description?.trim() ? item.description : null)
    && active === item.active;
  const canSave = !busy && !priceInvalid && !vatInvalid && !unchanged && (item !== null || Boolean(name.trim()));

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    const values = { price_minor: priceMinor, vat_rate: vatRate, unit: nextUnit, description: nextDescription };
    try {
      const next = item
        ? await cockpit.updateWorkflowCatalogItem(item.item_id, {
            expected_hash: item.source_hash,
            ...values,
            ...(manual ? { active } : {}),
          })
        : await cockpit.createWorkflowCatalogItem({ name: name.trim(), group: group.trim() || null, ...values });
      onSaved(next);
      pushToast({ tone: "success", title: t(item ? "catalog.saved" : "catalog.created") });
      onClose();
    } catch (caught) {
      const code = (caught as { data?: { code?: string } }).data?.code ?? "generic";
      if (item && code === "source_stale") {
        // The values typed here were for an article that has moved on; the way out is to open it again.
        onStale();
        setError(t("catalog.form.stale"));
      } else setError(t(`workflow.error.${code}`, undefined, t("workflow.error.generic")));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      tone="navy"
      icon={<Icon name={item ? "pencil" : "plus"} size={20} />}
      title={t(item ? "catalog.form.editTitle" : "catalog.add")}
      footer={(
        <div className="management-actions management-actions--end">
          <Button variant="ghost" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={busy} disabled={!canSave} onClick={save}>
            {t(item ? "workflow.business.save" : "catalog.add")}
          </Button>
        </div>
      )}
    >
      <div className={`stack-sm ${styles.form}`} data-testid="catalog-article-form">
        {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
        {item ? (
          <dl className={styles.fixed}>
            <div><dt>{t("catalog.form.name")}</dt><dd>{item.name}</dd></div>
            <div><dt>{t("catalog.form.group")}</dt><dd>{item.group?.trim() || t("catalog.group.none")}</dd></div>
            <div><dt>{t("catalog.col.number")}</dt><dd className="neo-mono">{item.bp_id ?? "—"}</dd></div>
          </dl>
        ) : <>
          <FormField label={t("catalog.form.name")} required>
            <Input value={name} maxLength={200} disabled={busy}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)} />
          </FormField>
          <FormField label={t("catalog.form.group")} helper={t("catalog.form.groupHelper")}>
            <Input value={group} maxLength={120} list={groupListId} disabled={busy}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setGroup(event.target.value)} />
          </FormField>
          <datalist id={groupListId}>
            {groupNames.map((groupName) => <option key={groupName} value={groupName} />)}
          </datalist>
        </>}
        <div className={styles.formRow}>
          <FormField label={t("workflow.business.price")} error={priceInvalid ? t("catalog.form.priceInvalid") : undefined}>
            <Input inputMode="decimal" value={price} disabled={busy} error={priceInvalid}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPrice(event.target.value)} />
          </FormField>
          <FormField label={t("workflow.business.vat")} error={vatInvalid ? t("catalog.form.vatInvalid") : undefined}>
            <Input inputMode="numeric" value={vat} disabled={busy} error={vatInvalid}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setVat(event.target.value)} />
          </FormField>
          <FormField label={t("workflow.business.unit")}>
            <Input value={unit} maxLength={64} disabled={busy}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setUnit(event.target.value)} />
          </FormField>
        </div>
        <FormField label={t("workflow.business.description")}>
          <Textarea value={description} maxLength={10000} disabled={busy}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(event.target.value)} />
        </FormField>
        {manual && (
          <Switch
            checked={active}
            disabled={busy}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setActive(event.currentTarget.checked)}
            label={t("catalog.form.active")}
            onLabel={t("businesses.catalog.flagActive")}
            offLabel={t("businesses.catalog.flagInactive")}
          />
        )}
        {item && !manual && <p className="subtle management-copy">{t("catalog.form.importedHint")}</p>}
      </div>
    </Dialog>
  );
}
